import React, { useEffect } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Badge,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  NotificationsNoneRounded as NotificationsIcon,
  DarkModeRounded as DarkModeIcon,
  LightModeRounded as LightModeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'evaluator':
        return 'primary';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'evaluator':
        return 'Evaluador';
      case 'viewer':
        return 'Visualizador';
      default:
        return role;
    }
  };

  const getPageTitle = () => {
    if (location.pathname.startsWith('/evaluations/')) {
      return 'Detalle de Evaluación';
    }
    
    switch (location.pathname) {
      case '/dashboard':
        return 'Dashboard';
      case '/patients':
        return 'Gestión de Pacientes';
      case '/evaluations':
        return 'Evaluaciones';
      case '/profile':
        return 'Mi Perfil';
      default:
        return 'Sinaptya';
    }
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  // Cambiar el título del documento dinámicamente
  useEffect(() => {
    const pageTitle = getPageTitle();
    document.title = `Sinaptya - ${pageTitle}`;
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Barra azul estética */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        height: '4px',
        width: '100%'
      }} />

      {/* Barra de navegación */}
      <Box sx={{ 
        bgcolor: 'background.paper', 
        borderBottom: 1, 
        borderColor: 'divider',
        px: 2,
        py: 1
      }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={isActiveRoute('/dashboard') ? 'contained' : 'text'}
              startIcon={<DashboardIcon />}
              component={RouterLink}
              to="/dashboard"
              sx={{ minWidth: 'auto' }}
            >
              Dashboard
            </Button>
            <Button
              variant={isActiveRoute('/patients') ? 'contained' : 'text'}
              startIcon={<PeopleIcon />}
              component={RouterLink}
              to="/patients"
              sx={{ minWidth: 'auto' }}
            >
              Pacientes
            </Button>
            <Button
              variant={isActiveRoute('/evaluations') ? 'contained' : 'text'}
              startIcon={<AssessmentIcon />}
              component={RouterLink}
              to="/evaluations"
              sx={{ minWidth: 'auto' }}
            >
              Evaluaciones
            </Button>
            <Button
              variant={isActiveRoute('/profile') ? 'contained' : 'text'}
              startIcon={<PersonIcon />}
              component={RouterLink}
              to="/profile"
              sx={{ minWidth: 'auto' }}
            >
              Perfil
            </Button>
          </Box>

          {/* Iconos de tema, notificaciones y perfil */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]),
              borderRadius: 5,
              p: 0.5,
            }}
          >
            <Tooltip title={mode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}>
              <IconButton onClick={toggleMode} size="small">
                {mode === 'dark' ? (
                  <LightModeIcon fontSize="small" sx={{ color: 'warning.main' }} />
                ) : (
                  <DarkModeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Notificaciones">
              <IconButton size="small">
                <Badge color="error" variant="dot" overlap="circular">
                  <NotificationsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

            <Tooltip title="Mi cuenta">
              <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.25 }}>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: 'secondary.main',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.firstName?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* Menú del usuario */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem component={RouterLink} to="/profile" onClick={handleMenuClose}>
                <PersonIcon sx={{ mr: 1 }} />
                Mi Perfil
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Cerrar Sesión
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>



      {/* Contenido principal */}
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 2.5 } }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 